/**
 * App.js
 * Talkroute
 *
 * Created by Vel Genov on 12/8/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

import React from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { connect, useDispatch } from "react-redux";
import { ToastProvider } from "react-toast-notifications";
import os from "os";
import LoginPage from "@components/LoginPage";
import RootLayoutManager from "./RootLayoutManager";
import PrivateRoute from "@components/PrivateRoute";
import routes from "../constants/routes";
import { CustomToast, Container } from "@components/toast/ToastComponents";
import FooterBanner from "./FooterBanner";
import { hot } from "react-hot-loader/root";
import { DialogContent, DialogTitle, ThemeProvider } from "@material-ui/core";
import theme from "../theme";
import { Meeting } from "@components/Meeting";
import { refreshPhonenumbers } from "@reducers/contactsReducer";
import Dialog from "@components/PresentationComponents/Dialog/Dialog";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import DialogActions from "@components/PresentationComponents/Dialog/DialogActions";
import DialogButton from "@components/PresentationComponents/Dialog/DialogButton";
import { AuthProvider, useAuthContext } from "../context/AuthProvider";

interface IAppProps {
  history: any;
  isUserAuthenticated: boolean;
  onMount?: () => any;
  titleBar?: any;
  notificationManager?: any;
  children?: any;
  fetchContactsDataUpdates: (background?: boolean | undefined) => Promise<void>;
}

/**
 * Entry point for the user end of the application
 */
class App extends React.Component<IAppProps, {}> {
  /**
   * Lifecycle function
   * Called when the react component mounts
   */
  componentDidMount() {
    if (this.props.onMount) {
      this.props.onMount();
    }
  }

  async componentDidUpdate(prevProps) {
    if (!!prevProps.isUserAuthenticated && !this.props.isUserAuthenticated) {
      this.props.history.push(routes.LOGIN);
    } else if (
      !prevProps.isUserAuthenticated &&
      !!this.props.isUserAuthenticated
    ) {
      this.props.history.push(routes.HOME);
      await this.props.fetchContactsDataUpdates();
    };
  };

  render() {
    const { isUserAuthenticated } = this.props;
    const rootHeight = os.platform() === "win32" ? "calc(100% - 28px)" : "100%";

    return (
      <>
        <ThemeProvider theme={theme}>
          <AuthProvider>
          <ToastProvider
            components={{ Toast: CustomToast, ToastContainer: Container }}
            placement="bottom-center"
          >
            {this.props.notificationManager}
            {this.props.titleBar}
            <div style={{ height: rootHeight }}>
              <ConnectedRouter history={this.props.history}>
                <Switch>
                  <Route path={routes.MEETINGS_IFRAME} component={Meeting} />
                  <Route path={routes.LOGIN} component={LoginPage} />
                  <PrivateRoute
                    path="/preferences/*"
                    component={RootLayoutManager}
                    isUserAuthenticated={isUserAuthenticated}
                  />
                  <PrivateRoute
                    path="/home/*"
                    component={RootLayoutManager}
                    isUserAuthenticated={isUserAuthenticated}
                  />
                  <Route path="*">
                    <Redirect to={routes.HOME} />
                  </Route>
                </Switch>
              </ConnectedRouter>
              <FooterBanner />
            </div>
          </ToastProvider>
          {this.props.children}
          <IsReconnectingInfoDialog />
          </AuthProvider>
        </ThemeProvider>
      </>
    );
  }
}

const IsReconnectingInfoDialog = () => {
  const refreshRetries = false; // useAppSelector((state) => state.authentication.oauthRefreshTries);
  const {logout} = useAuthContext();
  return (
    <Dialog
     showLoaderNextToTitle
     open={false}
     dismissable={false}
     title="Talkroute is updating...">
      <DialogContent>
        This process may take up to 5 minutes. If you do not wish to wait, please log out and log back in. Logging out will not impact the update.
      </DialogContent>
      <DialogActions>
        <DialogButton label="Logout UPDATE" onClick={() => 
          logout()
        }/>
      </DialogActions>
    </Dialog>
  )
}

// Redux data binding
const mapStateToProps = (state) => ({
  isUserAuthenticated: state.authentication.isUserAuthenticated,
});
const mapDispatchToProps = (dispatch) => ({
  fetchContactsDataUpdates: () => dispatch(refreshPhonenumbers())
});

export default hot(connect(mapStateToProps, mapDispatchToProps)(App));
